import appLogger from '@/utils/logger';
import React, {
  useCallback, useEffect, useMemo, useRef,
} from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useFormContext } from 'react-hook-form';
import { useDnDGraph, useDnDUI } from '../../contexts/DnDContext';
import { useNodeFormSync } from '../../hooks/useNodeFormSync';
import {
  IPlayNodeForm, HeyReachStep, HeyReachStepActionType, HeyReachMessageMode,
} from '@/types/plays';
import { LINKEDIN_OUTREACH_LABEL } from '@/utils/playDisplayNames';
import ShadCnFormSelect from '@/components/FormComponents/ShadCnFormSelect';
import ShadCnFormInput from '@/components/FormComponents/ShadCnFormInput';
import ShadCnFormTextarea from '@/components/FormComponents/ShadCnFormTextarea';
import ShadCnFormCheckbox from '@/components/FormComponents/ShadCnFormCheckbox';
import FormMultiSelect from '@/components/FormComponents/FormMultiSelect';
import { IMultiSelectOption } from '@/components/MultipleSelect';
import LinkedInSenderRoutingSection, {
  LinkedInSenderSelectionConfig,
} from './LinkedInSenderRoutingSection';
import {
  useGetHeyReachCampaigns,
  useGetHeyReachAccounts,
} from '@/hooks/useIntegrationsQuery';
import { isLinkedInSenderExemptAudience, needsLinkedInSender, parseLegacyCampaignPin } from '@/utils/heyreachWorkflowValidation';
import { useGetPlayDetails } from '@/hooks/usePlaysQuery';
import { useListTemplates } from '@/hooks/useTemplatesQuery';
import { IMessageTemplate } from '@/types/templates';
import {
  VIEW_BEFORE_CONNECT_EXPLANATION,
  hasViewProfileBefore,
  requestsAfter,
} from '@/utils/linkedinViewBeforeConnect';
import { isPhase2LinkedInStep, phase2LinkedInStepLabel } from '@/utils/phase2TemplateSteps';
import {
  buildNativeCustomDetails,
  buildNativeStepForms,
  isFrozenNativeSequence,
  isNativeLinkedInSequence,
} from '@/utils/nativeLinkedInSequence';
import NativeLinkedInSequencePanel from './NativeLinkedInSequencePanel';
import { useWorkspace } from '@/contexts/WorkspaceProvider';

interface IHeyReachNodeOverviewProps {
  actualConnectionId?: number;
  audienceId?: string | number;
}

// Matches a fill-in-the-blank token like "[First Name]" or "[Company Name]" — never a valid
// LinkedIn merge field. Mirrors the pre-send guard in audience-processor's emailRecipientWorker
// (findUnresolvedTemplateTokens) and the mb-server publish-time gate
// (getHeyReachNodesWithBracketPlaceholders) so all three surfaces agree on what counts as a
// placeholder-shaped bracket vs. incidental text like a footnote "[1]".
export const BRACKET_PLACEHOLDER_PATTERN = /\[[A-Z][a-zA-Z]*([ _][A-Za-z]+){0,3}\]/;

// #2183: One HeyReach canvas node carries exactly ONE LinkedIn touch. The
// catalog drives the step picker dropdown and the inline editor below. Order
// reflects the common-path priority — outreach actions first, then branches,
// then the terminal stop-on-reply. In-sequence delay (`heyreach_delay`) was
// removed: cross-step waiting is expressed by interleaving the platform
// `delay` node between HeyReach canvas nodes.
export const HEYREACH_STEP_CATALOG: Array<{
  type: HeyReachStepActionType;
  label: string;
  description: string;
  fields: Array<'message' | 'branch_timeout' | 'subject'>;
}> = [
  { type: 'heyreach_view_profile', label: 'View profile', description: 'View the prospect\'s LinkedIn profile', fields: [] },
  { type: 'heyreach_like_post', label: 'Like recent post', description: 'Like the prospect\'s most recent LinkedIn post', fields: [] },
  { type: 'heyreach_follow', label: 'Follow', description: 'Follow the prospect\'s LinkedIn profile', fields: [] },
  { type: 'heyreach_send_connection_request', label: 'Send connection request', description: 'Send a LinkedIn connection request, with an optional note', fields: ['message'] },
  { type: 'heyreach_send_message', label: 'Send message', description: 'Send a LinkedIn message. Only reaches prospects you are already connected to — add a connection request step first if they are not.', fields: ['message'] },
  // #5025 — real HeyReach nodes (unlike the retired branch markers below,
  // which the compiler always reported unsupported). Must be authored as an
  // adjacent pair: a "Check open profile" node immediately followed by a
  // "Send InMail" node — two separate canvas nodes, same as connection
  // request → message already chain. Saving them out of order or alone is
  // rejected by the server at publish time with a specific error, not
  // silently accepted.
  { type: 'heyreach_check_is_open_profile', label: 'Check open profile', description: 'Branch on LinkedIn Open Profile / InMail eligibility. Add a "Send InMail" step immediately after this one — if the prospect has an open profile, the InMail sends; otherwise the sequence falls through to the next step (typically a connection request).', fields: [] },
  { type: 'heyreach_send_inmail', label: 'Send InMail', description: 'Send a LinkedIn InMail — requires a sender with an authenticated Sales Navigator session, and a "Check open profile" step immediately before this one.', fields: ['subject', 'message'] },
];

/**
 * Retired step types, kept ONLY so a workflow saved before they were withdrawn
 * still renders its own steps with a readable label instead of a raw slug.
 *
 * They are not offered any more (Sunder, 2026-08-13: "let's remove all the
 * branches… I just want to make it super simple. View, like, follow, send").
 * None of them ever reached LinkedIn: the sequence compiler has always reported
 * every one as `unsupported` and dropped it, because HeyReach models acceptance
 * as a CONNECTION_REQUEST's own conditional branch and stop-on-reply as a
 * campaign-level setting — neither is a sequence node. So the builder was
 * offering controls that silently did nothing.
 */
export const HEYREACH_RETIRED_STEP_CATALOG: typeof HEYREACH_STEP_CATALOG = [
  { type: 'heyreach_branch_accepted', label: 'Branch: connection accepted (retired)', description: 'No longer supported — acceptance is handled on the connection request itself', fields: ['branch_timeout'] },
  { type: 'heyreach_branch_replied', label: 'Branch: reply received (retired)', description: 'No longer supported', fields: ['branch_timeout'] },
  { type: 'heyreach_branch_already_connected', label: 'Branch: already connected (retired)', description: 'No longer supported', fields: ['branch_timeout'] },
  { type: 'heyreach_branch_no_recent_post', label: 'Branch: no recent post (retired)', description: 'No longer supported', fields: ['branch_timeout'] },
  { type: 'heyreach_stop_on_reply', label: 'Stop on reply (retired)', description: 'No longer supported as a step — stop-on-reply is a campaign-level setting', fields: [] },
];

// Lookup spans BOTH catalogs: a saved workflow may still reference a retired
// step, and it must render with its label rather than crashing or showing a slug.
const HEYREACH_STEP_CATALOG_BY_TYPE: Record<HeyReachStepActionType, typeof HEYREACH_STEP_CATALOG[number]> = [
  ...HEYREACH_STEP_CATALOG,
  ...HEYREACH_RETIRED_STEP_CATALOG,
].reduce(
  (acc, entry) => {
    acc[entry.type] = entry;
    return acc;
  },
  {} as Record<HeyReachStepActionType, typeof HEYREACH_STEP_CATALOG[number]>,
);

const HeyReachNodeOverview: React.FC<IHeyReachNodeOverviewProps> = ({
  actualConnectionId,
  audienceId,
}) => {
  const form = useFormContext<IPlayNodeForm>();
  const { selectedNode } = useDnDUI();
  const { nodes, edges } = useDnDGraph();
  const initializedRef = useRef(false);
  // The step this node had when the panel opened: a request that already lacked a View profile
  // step is warned about; one the user is choosing right now is about to get one from the server.
  const openedAsStepRef = useRef<string | undefined>(undefined);
  // True while hydration writes the form. buildCustomDetails is a lossy
  // projection of legacy payloads (single-step model, ai_prompt-only routing,
  // scalar→pool senders), so it must never run from hydration-driven watcher
  // passes — merely opening a node would rewrite its persisted details
  // (mb-server replaces arrays on merge, truncating legacy multi-step rows).
  // The projection may only fire on an explicit user edit.
  const hydratingRef = useRef(false);
  // A step cloned from an outreach template (mb-frontend#4638) is shown, never written: any
  // edit here would add steps[] (the worker then treats it as a legacy play whose acceptance
  // gate drops everyone who doesn't accept from the template's later emails) and clear the
  // campaign pin the template delivers through.
  const selectedDetails = ((selectedNode as any)?.custom_action_details || {}) as Record<string, unknown>;
  const managedByTemplate = isPhase2LinkedInStep((selectedNode as any)?.action_type, selectedDetails);
  // A native LinkedIn sequence node (custom_action_details.native_sequence) carries the invite AND
  // every message after it. buildCustomDetails would save it back as one step, so it never runs for
  // these nodes: they get their own panel and builder, which keep every step, and nothing is ever
  // written once people are enrolled (native_sequence.frozen_at).
  const nativeSequence = isNativeLinkedInSequence(selectedDetails);
  const nativeFrozen = isFrozenNativeSequence(selectedDetails);
  // The native node's details as hydrated: edits are applied onto these, keeping every other key.
  const nativeBaseRef = useRef<Record<string, unknown> | null>(null);

  const buildDetails = ({ values, name }: { values: Partial<IPlayNodeForm>; name?: string }) => {
    if (hydratingRef.current || managedByTemplate) return null;
    if (nativeSequence) {
      // Only an edit to the sequence itself writes it; stray writes to the shared single-step
      // fields (e.g. the message-mode reset below) must not re-save a native node.
      if (!name?.startsWith('heyreach.native_steps') && name !== 'heyreach.native_sender_id') return null;
      return buildNativeCustomDetails(
        nativeBaseRef.current ?? selectedDetails,
        values.heyreach?.native_steps,
        values.heyreach?.native_sender_id,
      );
    }
    return buildCustomDetails(values);
  };

  const { withBatch } = useNodeFormSync({
    actualConnectionId,
    match: (name) => !!name && name.startsWith('heyreach.'),
    skipIfPristine: false,
    buildLocalDetails: buildDetails,
    buildPatchDetails: buildDetails,
  });

  const {
    data: campaigns,
  } = useGetHeyReachCampaigns();
  const { data: accounts, isLoading: loadingAccounts } = useGetHeyReachAccounts();
  // Repointing a LinkedIn sequence at a teammate's LinkedIn is an admin's call, same as email.
  const isWorkspaceAdmin = useWorkspace()?.isAdmin === true;
  // A native sequence is delivered through the credential that wins delivery (the workspace's
  // managed connection over a personal one), so its picker offers that credential's seats.
  const { data: deliveryAccounts } = useGetHeyReachAccounts(
    nativeSequence && !nativeFrozen && isWorkspaceAdmin,
    undefined,
    'delivery',
  );
  // Saved LinkedIn templates (same message_templates library the Chrome-extension
  // manual-send flow already uses), scoped to the LinkedIn DM channel — connection
  // requests and follow-up messages share this library rather than getting their
  // own channel, matching how a saved template is just reusable copy either way.
  const { data: linkedInTemplates } = useListTemplates({ channel: 'linkedin_dm' });
  // The campaign owner, whose LinkedIn a new step starts on (not the editor's, when an admin edits
  // a teammate's campaign). Same query the builder page already ran, so this is a cache hit.
  const numericAudienceId = Number(audienceId);
  const { data: playDetails } = useGetPlayDetails(
    Number.isInteger(numericAudienceId) && numericAudienceId > 0 ? numericAudienceId : 0,
  );
  const campaignOwnerUserId = playDetails?.audienceDetails?.user_id;
  // Signal templates/system workflows pick each signal's sender at send time (mb-server agrees).
  const senderExempt = isLinkedInSenderExemptAudience(playDetails?.audienceDetails);
  // A node with a database id is saved; mb-server already started it on the owner's seat when it
  // was created, and this panel must never rewrite a saved node just because it was opened.
  const isSavedNode = typeof actualConnectionId === 'number' && actualConnectionId > 0;
  const ownerSeatDefaultedRef = useRef(false);
  const ownerSeatTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Closing the panel (or switching nodes) before the default lands must not write into the next node.
  useEffect(() => () => clearTimeout(ownerSeatTimerRef.current), []);

  // Hydrate once from the persisted custom_action_details.
  useEffect(() => {
    if (initializedRef.current) return;
    if (!selectedNode || actualConnectionId !== (selectedNode as any).connectionId) return;

    appLogger.info('[HeyReachNodeOverview] Hydrating form from selectedNode', {
      connectionId: (selectedNode as any).connectionId,
      custom_action_details: (selectedNode as any).custom_action_details,
    });

    const customDetails = (selectedNode as any).custom_action_details || {};

    if (isNativeLinkedInSequence(customDetails)) {
      nativeBaseRef.current = customDetails;
      hydratingRef.current = true;
      try {
        withBatch(() => {
          form.setValue('heyreach.native_steps', buildNativeStepForms(customDetails));
          const pinned = normalizeLinkedInSenderIds(customDetails)[0];
          form.setValue('heyreach.native_sender_id', pinned === undefined ? undefined : String(pinned));
        });
      } finally {
        hydratingRef.current = false;
      }
      initializedRef.current = true;
      return;
    }
    nativeBaseRef.current = null;

    // Replace, never merge: the builder shares ONE react-hook-form instance
    // across canvas nodes, so hydration must write EVERY heyreach field —
    // conditionally setting only the fields present in this node's details
    // leaks the previously selected node's campaign pin, step, or message
    // into this node's next full-details patch (skipIfPristine: false means
    // that patch fires on open). withBatch collapses the setValue storm into
    // a single guarded sync pass over the fully-hydrated state.
    const hydrated = buildHeyReachFormValues(customDetails);
    openedAsStepRef.current = hydrated.step_action_type;
    // hydratingRef stays true through withBatch's trailing sync pass, so
    // hydration produces NO local-details update and NO backend patch — the
    // node's persisted payload is untouched until the user actually edits.
    hydratingRef.current = true;
    try {
      withBatch(() => {
        form.setValue('heyreach.campaign_id', hydrated.campaign_id);
        form.setValue('heyreach.linkedInAccountIds', hydrated.linkedInAccountIds);
        form.setValue('heyreach.linkedin_sender_selection', hydrated.linkedin_sender_selection);
        form.setValue('heyreach.step_action_type', hydrated.step_action_type);
        form.setValue('heyreach.message_mode', hydrated.message_mode);
        form.setValue('heyreach.message_template', hydrated.message_template);
        form.setValue('heyreach.subject', hydrated.subject);
        form.setValue('heyreach.message_guidance', hydrated.message_guidance);
        // Picker is a one-shot "apply this template's body" action, never persisted
        // — reset it so switching nodes doesn't show the last-picked template's name.
        form.setValue('heyreach.template_picker', undefined);
        form.setValue('heyreach.wait_timeout_hours', hydrated.wait_timeout_hours);
      });
    } finally {
      hydratingRef.current = false;
    }

    initializedRef.current = true;
  }, [selectedNode, actualConnectionId, form, withBatch]);

  const selectedStepType = form.watch('heyreach.step_action_type') as HeyReachStepActionType | undefined;
  const stepEntry = selectedStepType ? HEYREACH_STEP_CATALOG_BY_TYPE[selectedStepType] : undefined;

  // Why a View profile step sits before a connection request (the server adds one in front of
  // every new request, so the employment check sees the profile before the request goes out).
  const canvasNodeId = (selectedNode as { reactFlowNodeId?: string; nodeId?: string } | undefined)?.reactFlowNodeId
    ?? (selectedNode as { nodeId?: string } | undefined)?.nodeId;
  const viewProfileNote = useMemo(() => {
    if (!canvasNodeId) return null;
    if (selectedStepType === 'heyreach_view_profile') {
      return requestsAfter(canvasNodeId, nodes, edges).length > 0 ? 'covers_request' : null;
    }
    if (selectedStepType !== 'heyreach_send_connection_request') return null;
    if (hasViewProfileBefore(canvasNodeId, nodes, edges)) return 'covered';
    return openedAsStepRef.current === 'heyreach_send_connection_request' ? 'missing' : 'will_add';
  }, [canvasNodeId, selectedStepType, nodes, edges]);
  const messageMode = (form.watch('heyreach.message_mode') as HeyReachMessageMode | undefined) ?? 'ai';
  const messageTemplate = form.watch('heyreach.message_template') as string | undefined;
  // LinkedIn only resolves {{first_name}}-style tokens — a [Bracket Placeholder] left in the
  // message body (e.g. from a habit carried over from the product's OTHER placeholder syntax,
  // used in cold-call scripts) is sent to the prospect literally. Confirmed live 2026-08-15: an
  // AI-authored LinkedIn touch shipped with "Hi [First Name] ..." before this warning existed.
  const hasBracketPlaceholder = Boolean(messageTemplate && BRACKET_PLACEHOLDER_PATTERN.test(messageTemplate));

  // "No note" is only meaningful on a connection request — a message step with no
  // body would send nothing at all.
  const messageModeOptions = useMemo(() => {
    const options = [
      { label: 'Let AI write it for each prospect', value: 'ai' },
      { label: 'Same message for everyone', value: 'static' },
    ];
    if (supportsNoNote(selectedStepType)) {
      options.push({ label: 'Send with no note', value: 'none' });
    }
    return options;
  }, [selectedStepType]);

  // Switching to a step type that cannot be note-free must not leave the node in
  // an unsendable 'none' mode.
  useEffect(() => {
    if (messageMode === 'none' && !supportsNoNote(selectedStepType)) {
      form.setValue('heyreach.message_mode', 'ai');
    }
  }, [messageMode, selectedStepType, form]);
  const selectedSenderIds = form.watch('heyreach.linkedInAccountIds') || [];
  const senderSelection = form.watch(
    'heyreach.linkedin_sender_selection',
  ) as LinkedInSenderSelectionConfig | null | undefined;
  // Hidden legacy pin (no UI sets it — hydrated from persisted details).
  // Opening the node preserves it without writing. The first intentional edit
  // retires it in buildCustomDetails(), moving the node to a managed campaign.
  const legacyCampaignId = form.watch('heyreach.campaign_id');

  const legacyCampaign = useMemo(
    () => (legacyCampaignId
      ? (campaigns || []).find(
        (campaign: any) => String(campaign.id) === String(legacyCampaignId),
      )
      : undefined),
    [campaigns, legacyCampaignId],
  );

  const senderOptions = useMemo(
    () => buildSenderOptions(accounts?.accounts || [], selectedSenderIds),
    [accounts, selectedSenderIds],
  );
  const seatlessCount = accounts?.membersWithoutSeat?.length ?? 0;

  const selectedSenders = useMemo(() => {
    const accountMap = new Map((accounts?.accounts || []).map((account: any) => [String(account.id), account]));
    return selectedSenderIds.map((id) => {
      const account: any = accountMap.get(String(id));
      if (!account || account.authIsValid === false) return null;
      return {
        id: Number(id),
        name: account.name || `LinkedIn sender ${id}`,
      };
    }).filter(
      (sender): sender is { id: number; name: string } => (
        sender !== null && Number.isInteger(sender.id) && sender.id > 0
      ),
    );
  }, [accounts, selectedSenderIds]);

  const invalidSelectedSenders = useMemo(() => {
    const accountMap = new Map((accounts?.accounts || []).map((account: any) => [String(account.id), account]));
    return selectedSenderIds.filter((id) => {
      const account: any = accountMap.get(String(id));
      return !account || account.authIsValid === false;
    });
  }, [accounts, selectedSenderIds]);

  // A new LinkedIn step starts on the campaign owner's own seat. A sender left empty used to mean
  // every LinkedIn account in the workspace (Jumio, 2026-10-16). Only a node not yet saved is
  // defaulted here (it is created from this form); a saved one is never rewritten on open, which
  // would also move people mid-sequence onto an account they never connected with.
  useEffect(() => {
    if (ownerSeatDefaultedRef.current || !initializedRef.current) return;
    if (isSavedNode || senderExempt || nativeSequence || managedByTemplate || loadingAccounts) return;
    const seatId = defaultOwnerSeatId({
      details: selectedDetails,
      accounts: accounts?.accounts,
      ownerUserId: campaignOwnerUserId,
    });
    if (seatId === undefined) return;
    ownerSeatDefaultedRef.current = true;
    // Next tick, not this commit: the form sync reads a stale snapshot of the form for writes made
    // in the same pass that opened the panel, and would save the step without the seat.
    ownerSeatTimerRef.current = setTimeout(() => {
      ownerSeatTimerRef.current = undefined;
      if ((form.getValues('heyreach.linkedInAccountIds') || []).length > 0) return;
      form.setValue('heyreach.linkedInAccountIds', [seatId], { shouldDirty: true, shouldTouch: true });
    }, 0);
  }, [
    accounts, campaignOwnerUserId, form, isSavedNode, loadingAccounts, managedByTemplate, nativeSequence,
    selectedDetails, senderExempt,
  ]);

  const handleSenderRoutingChange = useCallback(
    (config: LinkedInSenderSelectionConfig | null) => {
      form.setValue('heyreach.linkedin_sender_selection', config, {
        shouldDirty: true,
        shouldTouch: true,
      });
    },
    [form],
  );

  const templateOptions = useMemo(
    () => (linkedInTemplates || []).map((template: IMessageTemplate) => ({
      label: template.name,
      value: template.template_id,
    })),
    [linkedInTemplates],
  );

  const handleSelectTemplate = useCallback(
    (templateId: string) => {
      const template = (linkedInTemplates || []).find((t: IMessageTemplate) => t.template_id === templateId);
      if (!template) return;
      form.setValue('heyreach.message_template', template.body, { shouldDirty: true, shouldTouch: true });
    },
    [linkedInTemplates, form],
  );

  if (nativeSequence) {
    const senderId = normalizeLinkedInSenderIds(selectedDetails)[0];
    const sender: any = senderId === undefined
      ? undefined
      : (accounts?.accounts || []).find((account: any) => String(account.id) === String(senderId));
    return (
      <NativeLinkedInSequencePanel
        details={selectedDetails}
        frozen={nativeFrozen}
        senderLabel={
          senderId === undefined
            ? 'No sender selected'
            : sender?.name || `LinkedIn sender ${String(senderId)}`
        }
        senderOptions={isWorkspaceAdmin && deliveryAccounts
          ? buildNativeSenderOptions(deliveryAccounts.accounts, senderId === undefined ? undefined : String(senderId))
          : undefined}
      />
    );
  }

  if (managedByTemplate) {
    const purpose = typeof selectedDetails.purpose === 'string' ? selectedDetails.purpose.trim() : '';
    return (
      <div className="p-4 flex flex-col gap-4">
        <div className="flex items-center gap-2 mb-2">
          <Image src="https://app.heyreach.io/assets/images/logo/heyreach-icon.svg" alt="LinkedIn Outreach" className="h-5 w-5 object-contain" width={20} height={20} />
          <h3 className="text-sm font-medium text-gray-900">{LINKEDIN_OUTREACH_LABEL}</h3>
        </div>
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
          <strong>Managed by the outreach template.</strong>{' '}
          This step runs as part of the template&apos;s LinkedIn sequence, so it can&apos;t be edited here.
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-gray-500">Step</span>
          <span className="text-sm text-gray-900">{phase2LinkedInStepLabel(selectedDetails)}</span>
        </div>
        {purpose && (
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-gray-500">Purpose</span>
            <p className="text-sm text-gray-900 whitespace-pre-wrap">{purpose}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="p-4 flex flex-col gap-4">
      <div className="flex items-center gap-2 mb-2">
        <Image src="https://app.heyreach.io/assets/images/logo/heyreach-icon.svg" alt="LinkedIn Outreach" className="h-5 w-5 object-contain" width={20} height={20} />
        <h3 className="text-sm font-medium text-gray-900">{LINKEDIN_OUTREACH_LABEL}</h3>
      </div>

      {legacyCampaignId && (
        <div className='rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600'>
          <strong>Legacy campaign:</strong>{' '}
          {legacyCampaign?.name || `LinkedIn campaign #${String(legacyCampaignId)}`}.
          {' '}Any connected sender can be selected. Saving a change moves
          this node to a MarketBetter-managed campaign.
        </div>
      )}

      <FormMultiSelect
        name='heyreach.linkedInAccountIds'
        label='Send from'
        placeholder='Choose a LinkedIn sender'
        options={senderOptions}
        maxCount={4}
        disabled={loadingAccounts}
      />

      {!loadingAccounts && !senderExempt && needsLinkedInSender('heyreach_campaign', {
        campaign_id: legacyCampaignId,
        linkedInAccountIds: selectedSenderIds,
        steps: selectedStepType ? [{ action_type: selectedStepType }] : [],
      }) && (
        <div className='rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900'>
          <strong>Choose who this sends from.</strong>{' '}
          {describeAllSeatsFallback((accounts?.accounts || []).filter((account: any) => account.authIsValid !== false).length)}
          {' '}You can&apos;t launch until a sender is picked.
        </div>
      )}

      {!loadingAccounts && seatlessCount > 0 && (
        // The invite itself lives in Settings (mb-frontend#4719): account setup
        // is not a workflow-node concern, and an AE cannot request a seat here.
        <p className='-mt-2 text-xs text-slate-500'>
          {describeSeatlessMembers(seatlessCount)}{' '}
          <Link href='/settings?tab=integrations' className='font-medium text-blue-600 hover:underline'>
            Invite them in Settings › Integrations › LinkedIn Outreach
          </Link>
          .
        </p>
      )}

      {!loadingAccounts && invalidSelectedSenders.length > 0 && (
        <div className='rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900'>
          {invalidSelectedSenders.length} selected sender
          {invalidSelectedSenders.length === 1 ? ' is' : 's are'} unavailable.
          MarketBetter excludes {invalidSelectedSenders.length === 1 ? 'it' : 'them'} at runtime;
          remove or replace {invalidSelectedSenders.length === 1 ? 'it' : 'them'} so the
          selected pool always has an eligible sender.
        </div>
      )}

      <LinkedInSenderRoutingSection
        config={senderSelection}
        availableSenders={selectedSenders}
        onChange={handleSenderRoutingChange}
      />

      <div className="flex flex-col gap-1 border-t border-gray-100 pt-4">
        <ShadCnFormCheckbox
          name="heyreach.send_on_weekends"
          label="Also send on Saturday and Sunday"
        />
        <p className="text-xs text-gray-500">
          LinkedIn sends 9am–5pm in the audience timezone. Weekends add sending days, not
          sending hours — useful before an event date, because LinkedIn limits invites per
          day, per sender. Applies to this node&apos;s campaign when it is next provisioned
          or its steps change.
        </p>
      </div>

      <div className="flex flex-col gap-3 border-t border-gray-100 pt-4">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-gray-900">LinkedIn step</h4>
        </div>
        <p className="text-xs text-gray-500">
          One LinkedIn touch per LinkedIn Outreach node. To send multiple touches, drop additional LinkedIn Outreach nodes after this one and use a Delay node in between to space them out.
        </p>

        <ShadCnFormSelect
          name="heyreach.step_action_type"
          label="Step"
          placeholder="Select a LinkedIn touch..."
          options={HEYREACH_STEP_CATALOG.map((entry) => ({
            label: entry.label,
            value: entry.type,
          }))}
        />

        {stepEntry?.description && (
          <p className="text-xs text-gray-500 -mt-1">{stepEntry.description}</p>
        )}

        {viewProfileNote === 'missing' ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
            <strong>No View profile step before this request.</strong>{' '}
            We can&apos;t confirm they still work at the company you&apos;re targeting before it goes out.
            Add a LinkedIn Outreach node set to &quot;View profile&quot;, then a 1-day Delay, before this one.
          </div>
        ) : viewProfileNote && (
          <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-800">
            {viewProfileNote === 'covered' && 'A View profile step runs a day before this request. '}
            {viewProfileNote === 'will_add' && 'A View profile step is added a day before this request when it saves. '}
            {VIEW_BEFORE_CONNECT_EXPLANATION}
          </div>
        )}

        {stepEntry?.fields.includes('subject') && (
          <>
            <ShadCnFormInput
              name="heyreach.subject"
              label="InMail subject"
              placeholder="Quick question about {{companyName}}"
              maxLength={200}
              required
            />
            <p className="text-xs text-gray-500 -mt-1">
              Required — LinkedIn caps InMail subjects at 200 characters.
            </p>
          </>
        )}

        {stepEntry?.fields.includes('message') && (
          <>
            <ShadCnFormSelect
              name="heyreach.message_mode"
              label={supportsNoNote(selectedStepType) ? 'Connection note' : 'Message'}
              placeholder="Choose how this message is written..."
              options={messageModeOptions}
            />

            {messageMode === 'ai' && (
              <>
                <p className="text-xs text-gray-500 -mt-1">
                  AI writes a different message for each prospect when the step runs, using
                  verified results from earlier workflow steps.
                </p>
                <ShadCnFormTextarea
                  name="heyreach.message_guidance"
                  label="Instructions for the AI (optional)"
                  placeholder="e.g. Mention their recent funding round. Keep it under two sentences. Don't pitch, just open a conversation."
                  rows={3}
                />
                <p className="text-xs text-gray-500 -mt-1">
                  Guides tone and angle without fixing the exact wording — every prospect
                  still gets a distinct message.
                </p>
              </>
            )}

            {messageMode === 'none' && (
              <p className="text-xs text-gray-500 -mt-1">
                Sends a plain invite with no note, which usually gets the highest acceptance
                rate. Add a &ldquo;Send message&rdquo; step after this one to reach out once
                they accept.
              </p>
            )}

            {messageMode === 'static' && (
              <>
                {templateOptions.length > 0 && (
                  <ShadCnFormSelect
                    name="heyreach.template_picker"
                    label="Start from a saved template (optional)"
                    placeholder="Select a template..."
                    options={templateOptions}
                    onChange={handleSelectTemplate}
                  />
                )}
                <ShadCnFormTextarea
                  name="heyreach.message_template"
                  label={isInmailStep(selectedStepType) ? 'InMail body' : 'Your message'}
                  placeholder="Hi, I came across your work and would like to connect..."
                  rows={4}
                  maxLength={
                    supportsNoNote(selectedStepType)
                      ? 300
                      : isInmailStep(selectedStepType) ? 1900 : undefined
                  }
                />
                <p className="text-xs text-gray-500 -mt-1">
                  Every prospect in this step receives this exact message.
                  {supportsNoNote(selectedStepType)
                    && ' LinkedIn caps connection notes at 300 characters.'}
                  {isInmailStep(selectedStepType)
                    && ' LinkedIn caps InMail bodies at 1,900 characters.'}
                </p>
                {hasBracketPlaceholder && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                    This looks like a <code className="font-mono">[Bracket]</code> placeholder — LinkedIn only
                    fills in <code className="font-mono">{'{{first_name}}'}</code>-style tokens. Text like{' '}
                    <code className="font-mono">[First Name]</code> will be sent to every prospect exactly as written.
                  </div>
                )}
              </>
            )}
          </>
        )}

        {stepEntry?.fields.includes('branch_timeout') && (
          <ShadCnFormInput
            name="heyreach.wait_timeout_hours"
            label="Wait timeout (hours)"
            type="number"
            min={0}
          />
        )}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
        <p className="text-xs text-blue-800">
          <strong>Cost:</strong> 20 credits per lead added to campaign.
          <br />
          Leads flow into this LinkedIn step from previous plays in the workflow.
        </p>
      </div>
    </div>
  );
};

/** Type guard for legacy / external payloads. */
function isValidStepActionType(t: unknown): t is HeyReachStepActionType {
  return typeof t === 'string' && t in HEYREACH_STEP_CATALOG_BY_TYPE;
}

/** The AI placeholder the audience-processor compiler recognises. */
const AI_CONTENT_TOKEN = '{{content}}';
// Must match the audience-processor's rule EXACTLY: requiresAiHeyReachContent() treats
// a template as AI-generated when it contains {{content}} ANYWHERE, not only when the
// whole body is the token. Anchoring this to the whole string made "Hi — {{content}}"
// open as "Same message for everyone" while dispatch still generated per-prospect copy.
const AI_CONTENT_PATTERN = /\{\{\s*content\s*\}\}/i;

/** Only connection requests can be sent with no note at all. */
export function supportsNoNote(stepType?: HeyReachStepActionType): boolean {
  return stepType === 'heyreach_send_connection_request';
}

/** InMail carries a subject and a longer (1,900-char) body cap — #5025. */
export function isInmailStep(stepType?: HeyReachStepActionType): boolean {
  return stepType === 'heyreach_send_inmail';
}

/**
 * Derive the side panel's message mode from a persisted step.
 *
 * Deriving rather than storing is what makes this change safe for existing
 * workflows: a play authored before the mode selector existed opens in the mode
 * that matches its current behavior, and re-saving it round-trips to the same
 * payload. No migration, no silent rewrite of live copy.
 */
export function deriveMessageMode(step?: Partial<HeyReachStep>): HeyReachMessageMode {
  if (!step) return 'ai';
  if (step.send_without_note === true) return 'none';
  const template = typeof step.message_template === 'string' ? step.message_template : '';
  // Blank compiles to {{content}} in the sequence compiler, so blank IS the AI mode.
  if (!template.trim() || AI_CONTENT_PATTERN.test(template)) return 'ai';
  return 'static';
}

/**
 * Build the sender picker's options from the connected seats. Names only,
 * never addresses: seats are personal logins and this picker is visible to
 * every SDR in the workspace. Teammates without a seat are not rows here any
 * more (mb-frontend#4719) — they are counted in one line under the picker
 * that links to Settings › Integrations › LinkedIn Outreach, where an admin
 * invites them. Naming them at all is what turned "his profile doesn't show
 * up" into "he has no seat yet" (mb-server#5478).
 */
/**
 * One line for the node panel when teammates lack a seat; the picker itself
 * lists real senders only (mb-frontend#4719).
 */
export function describeAllSeatsFallback(seatCount: number): string {
  return seatCount > 1
    ? `With no sender picked, it would send from all ${seatCount} LinkedIn accounts in the workspace.`
    : 'With no sender picked, it would send from any LinkedIn account in the workspace.';
}

/**
 * The seat a brand-new LinkedIn step starts on: the campaign owner's own, while it is connected.
 * Undefined for a step that already has a sender, a step, or a legacy campaign pin, and when the
 * owner has no usable seat (the panel then asks for one).
 */
export function defaultOwnerSeatId({
  details,
  accounts,
  ownerUserId,
}: {
  details: Record<string, unknown>;
  accounts: Array<Record<string, any>> | undefined;
  ownerUserId: string | null | undefined;
}): string | undefined {
  if (!ownerUserId || !accounts) return undefined;
  if (normalizeLinkedInSenderIds(details).length > 0) return undefined;
  if (Array.isArray(details.steps) && details.steps.length > 0) return undefined;
  if (parseLegacyCampaignPin(details.campaign_id)) return undefined;
  const seat = accounts.find(
    (account) => account.ownerUserId === ownerUserId && account.authIsValid !== false,
  );
  return seat ? String(seat.id) : undefined;
}

export function describeSeatlessMembers(count: number): string {
  return count === 1
    ? '1 teammate has no LinkedIn seat yet.'
    : `${count} teammates have no LinkedIn seat yet.`;
}

/**
 * The seats a native LinkedIn sequence can send from: every connected seat, plus the pinned one
 * even if it has since disconnected (so the picker still shows it).
 */
export function buildNativeSenderOptions(
  accounts: Array<Record<string, any>>,
  pinnedId: string | undefined,
): Array<{ label: string; value: string; name: string }> {
  const options = accounts
    .filter((account) => account.authIsValid !== false || String(account.id) === pinnedId)
    .map((account) => {
      const id = String(account.id);
      const name = String(account.name || `LinkedIn sender ${id}`);
      const suffix = account.authIsValid === false ? ' — reconnect required' : '';
      return { label: `${name}${suffix}`, value: id, name };
    });
  if (pinnedId !== undefined && !options.some((option) => option.value === pinnedId)) {
    options.push({ label: `LinkedIn sender ${pinnedId} — unavailable`, value: pinnedId, name: `LinkedIn sender ${pinnedId}` });
  }
  return options;
}

export function buildSenderOptions(
  accounts: Array<Record<string, any>>,
  selectedSenderIds: string[],
): IMultiSelectOption[] {
  const nameCounts = accounts.reduce<Record<string, number>>((counts, account) => {
    const name = String(account.name || '');
    return { ...counts, [name]: (counts[name] || 0) + 1 };
  }, {});

  const liveOptions = accounts.map((account) => {
    const selected = selectedSenderIds.includes(String(account.id));
    const unavailable = account.authIsValid === false;
    // Keep an invalid saved selection removable. Once removed it becomes disabled.
    const disabled = unavailable && !selected;
    const suffix = disabled
      ? ''
      : unavailable
        ? ' — reconnect required'
        : account.connectionCount
          ? ` (${account.connectionCount} campaigns)`
          : '';
    const ambiguous = nameCounts[String(account.name || '')] > 1;

    return {
      label: `${account.name}${ambiguous ? ` · sender ${account.id}` : ''}${suffix}`,
      value: String(account.id),
      disabled,
      disabledBadge: disabled ? 'Reconnect required' : undefined,
    };
  });
  const liveIds = new Set(liveOptions.map((option) => option.value));
  const staleSelectedOptions = selectedSenderIds
    .filter((id) => !liveIds.has(String(id)))
    .map((id) => ({
      label: `LinkedIn sender ${id} — unavailable`,
      value: String(id),
      // The only action for a stale selection is removal, so it must remain interactive.
      disabled: false,
    }));
  return [...liveOptions, ...staleSelectedOptions];
}

/**
 * Compute the COMPLETE heyreach form state for a node's persisted details.
 * Every field is always present — absent details reset to their empty value.
 * The builder shares one react-hook-form instance across canvas nodes, so
 * hydration must overwrite all fields; setting only present values leaks the
 * previous node's campaign pin, step, or message into the next node's patch.
 */
export function buildHeyReachFormValues(
  customDetails: Record<string, unknown>,
): Required<Pick<NonNullable<IPlayNodeForm['heyreach']>,
  'linkedInAccountIds' | 'linkedin_sender_selection' | 'message_mode' | 'message_template' | 'subject' | 'message_guidance'
>> & Pick<NonNullable<IPlayNodeForm['heyreach']>,
  'campaign_id' | 'step_action_type' | 'wait_timeout_hours' | 'send_on_weekends'
> {
  // Single step model: pick the first entry of any persisted steps[] (legacy
  // multi-step rows from the Wave 2 composite design are tolerated).
  const persistedSteps = Array.isArray(customDetails.steps) ? customDetails.steps : [];
  const firstStep = (persistedSteps[0] ?? {}) as Partial<HeyReachStep>;
  const stepType = isValidStepActionType(firstStep.action_type)
    ? firstStep.action_type
    : undefined;
  const selection = customDetails.linkedin_sender_selection as
    | { mode?: string; prompt?: string }
    | null
    | undefined;

  return {
    // Positive numeric IDs only — 0/'0'/whitespace artifacts must hydrate as
    // "no pin" so gating and validation treat the node as managed.
    campaign_id: parseLegacyCampaignPin(customDetails.campaign_id),
    linkedInAccountIds: normalizeLinkedInSenderIds(customDetails).map(String),
    linkedin_sender_selection:
      selection?.mode === 'ai_prompt' && typeof selection.prompt === 'string'
        ? { mode: 'ai_prompt', prompt: selection.prompt }
        : null,
    step_action_type: stepType,
    // Campaign-level, so it lives on the node and not inside steps[].
    send_on_weekends: customDetails.send_on_weekends === true,
    message_mode: deriveMessageMode(stepType ? firstStep : undefined),
    message_template:
      stepType && typeof firstStep.message_template === 'string'
        ? firstStep.message_template
        : '',
    subject:
      stepType && typeof firstStep.subject === 'string'
        ? firstStep.subject
        : '',
    message_guidance:
      stepType && typeof firstStep.message_guidance === 'string'
        ? firstStep.message_guidance
        : '',
    wait_timeout_hours:
      stepType && typeof firstStep.wait_timeout_hours === 'number'
        ? firstStep.wait_timeout_hours
        : undefined,
  };
}

export function normalizeLinkedInSenderIds(
  details: Record<string, unknown>,
): Array<number | string> {
  if (Array.isArray(details.linkedInAccountIds) && details.linkedInAccountIds.length > 0) {
    return details.linkedInAccountIds.filter(
      (id): id is number | string => Number.isInteger(Number(id)) && Number(id) > 0,
    );
  }
  if (Number.isInteger(Number(details.linkedInAccountId)) && Number(details.linkedInAccountId) > 0) {
    return [details.linkedInAccountId as number | string];
  }
  return [];
}

export function buildCustomDetails(values: Partial<IPlayNodeForm>): Record<string, unknown> {
  const linkedInAccountIds = [...new Set(
    (values.heyreach?.linkedInAccountIds || [])
      .map(Number)
      .filter((id) => Number.isInteger(id) && id > 0),
  )];
  const out: Record<string, unknown> = {
    // buildCustomDetails only runs on an intentional edit; hydration is
    // suppressed above. Retire any hidden legacy shared-campaign pin so the
    // edited node uses a managed campaign and every auth-healthy sender can
    // participate. Merely opening a node still performs no write.
    campaign_id: null,
    linkedInAccountIds,
    // Preserve the legacy scalar only when the pool has exactly one sender.
    // Existing consumers continue to work without accidentally pinning a
    // multi-sender workflow to the first account.
    linkedInAccountId: linkedInAccountIds.length === 1 ? linkedInAccountIds[0] : null,
    linkedin_sender_selection: values.heyreach?.linkedin_sender_selection || null,
    // buildCustomDetails replaces the whole details object, so a field omitted
    // here is silently dropped on every edit of the node.
    send_on_weekends: values.heyreach?.send_on_weekends === true,
  };

  const stepType = values.heyreach?.step_action_type;
  if (stepType && isValidStepActionType(stepType)) {
    const entry = HEYREACH_STEP_CATALOG_BY_TYPE[stepType];
    const step: HeyReachStep = { action_type: stepType };

    if (entry.fields.includes('subject')) {
      const subject = values.heyreach?.subject;
      if (typeof subject === 'string' && subject.trim().length > 0) {
        step.subject = subject.trim();
      }
    }

    if (entry.fields.includes('message')) {
      // The mode owns what gets persisted, so a customer never has to know that
      // `{{content}}` is what turns on per-recipient AI copy.
      //
      // When no mode is present the fallback must be DERIVED from the values we
      // already hold, never hardcoded: any caller that builds a payload without
      // going through the side panel (or before hydration sets the mode) would
      // otherwise have its authored copy silently replaced by {{content}}.
      const mode = values.heyreach?.message_mode ?? deriveMessageMode({
        action_type: stepType,
        message_template: values.heyreach?.message_template,
      });
      if (mode === 'none' && supportsNoNote(stepType)) {
        step.send_without_note = true;
      } else if (mode === 'static') {
        const msg = values.heyreach?.message_template;
        if (typeof msg === 'string' && msg.length > 0) step.message_template = msg;
      } else {
        // AI mode: keep a template that ALREADY carries the token, byte for byte.
        // Rewriting it to the bare token would drop surrounding copy ("Hi — {{content}}")
        // and normalize casing ({{Content}} → {{content}}) — and HeyReach field matching
        // is case-sensitive, so a casing change silently alters what the prospect sees.
        // Only synthesize the canonical token when there is nothing to preserve.
        const existing = values.heyreach?.message_template;
        step.message_template = typeof existing === 'string' && AI_CONTENT_PATTERN.test(existing)
          ? existing
          : AI_CONTENT_TOKEN;
      }
      // Guidance only steers AI-GENERATED copy. A static message is sent to
      // HeyReach literally (sequenceCompiler.js never substitutes generated
      // content into a template with no {{content}} token), so persisting
      // guidance in 'static' mode would silently do nothing — same reasoning
      // that already excludes 'none' (nothing to steer on a note-free invite).
      if (mode === 'ai') {
        const guidance = values.heyreach?.message_guidance;
        if (typeof guidance === 'string' && guidance.trim().length > 0) {
          step.message_guidance = guidance.trim();
        }
      }
    }
    if (entry.fields.includes('branch_timeout')) {
      const raw = values.heyreach?.wait_timeout_hours;
      if (raw !== undefined && raw !== null && `${raw}` !== '') {
        step.wait_timeout_hours = Number(raw);
      }
    }
    out.steps = [step];
  }

  return out;
}

export default HeyReachNodeOverview;
